import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaUserTie, FaGavel, FaGlobeAmericas, FaHandshake } from 'react-icons/fa';
import { useInView } from '../hooks/useInView';

const AboutPage: React.FC = () => {
  useEffect(() => {
    document.title = 'About Us | Janssen Law Professional Corporation';
  }, []);

  const storySection = useInView();
  const valuesSection = useInView();
  const historySection = useInView();
  const communitySection = useInView();

  return (
    <div className="about-page">
      <section className="about-hero">
        <h1>About Janssen Law</h1>
        <p>Nearly three decades of no-nonsense, results-oriented legal counsel.</p>
      </section>

      {/* Firm Story */}
      <section className="about-story">
        <div
          ref={storySection.ref}
          className={`about-story-inner ${storySection.isVisible ? 'visible' : ''}`}
        >
          <div className="story-content">
            <h2>Our Story</h2>
            <p>
              Janssen Law Professional Corporation was established in 1996 by Charlotte M. Janssen,
              bringing together decades of international legal experience to serve the Toronto
              community and beyond.
            </p>
            <p>
              Prior to founding the firm, Charlotte worked with Baker & McKenzie in Toronto,
              S&atilde;o Paulo, and London, gaining invaluable experience in international business
              law. This global perspective informs our approach to every matter we handle.
            </p>
            <p>
              Today, our team of lawyers and staff provides comprehensive legal services across
              business law, real estate, immigration, and wills & estates. We pride ourselves on
              being consistent with in-house counsel, offering a vast array of legal advice to
              business owners and high net worth clients.
            </p>
          </div>
          <div className="story-highlight">
            <div className="highlight-stat">
              <span className="stat-number">1996</span>
              <span className="stat-label">Year Established</span>
            </div>
            <div className="highlight-stat">
              <span className="stat-number">30+</span>
              <span className="stat-label">Years of Experience</span>
            </div>
            <div className="highlight-stat">
              <span className="stat-number">4</span>
              <span className="stat-label">Practice Areas</span>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="about-values">
        <div
          ref={valuesSection.ref}
          className={`about-values-inner ${valuesSection.isVisible ? 'visible' : ''}`}
        >
          <div className="values-header">
            <h2>Our Approach</h2>
            <p>What sets Janssen Law apart from the rest.</p>
          </div>
          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon"><FaUserTie /></div>
              <h3>Client-Focused</h3>
              <p>Charlotte Janssen personally oversees every case, ensuring dedicated attention and accountability at every stage.</p>
            </div>
            <div className="value-card">
              <div className="value-icon"><FaGavel /></div>
              <h3>Results-Driven</h3>
              <p>Our no-nonsense approach means we focus on practical solutions and measurable outcomes for our clients.</p>
            </div>
            <div className="value-card">
              <div className="value-icon"><FaGlobeAmericas /></div>
              <h3>Global Experience</h3>
              <p>International experience across three continents provides a unique perspective on cross-border legal matters.</p>
            </div>
            <div className="value-card">
              <div className="value-icon"><FaHandshake /></div>
              <h3>Comprehensive Service</h3>
              <p>From corporate transactions to immigration to estate planning, we handle a broad range of interconnected legal needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="about-history">
        <div
          ref={historySection.ref}
          className={`about-history-inner ${historySection.isVisible ? 'visible' : ''}`}
        >
          <div className="history-header">
            <h2>Our History</h2>
            <p>Key milestones in the firm's journey.</p>
          </div>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-year">1986</div>
              <h3>Education Complete</h3>
              <p>Charlotte Janssen graduates from the University of Toronto with a J.D., following an M.A. in Economics from Carleton University and a B.A. Honours from the University of Manitoba.</p>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">1988</div>
              <h3>Called to the Bar</h3>
              <p>Admitted to the Law Society of Upper Canada. Begins practice with Baker & McKenzie, working in Toronto, S&atilde;o Paulo, and London.</p>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">Early 1990s</div>
              <h3>Baker & Janssen</h3>
              <p>Becomes a partner at Baker & Janssen, expanding expertise in international business law and corporate transactions.</p>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">1996</div>
              <h3>Janssen Law Founded</h3>
              <p>Establishes Janssen Law Professional Corporation, bringing decades of international experience to an independent practice serving Toronto's business community.</p>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2012</div>
              <h3>International Recognition</h3>
              <p>Serves as Canadian Representative to The Hague Conference on International Law on Foreign Laws in Civil and Commercial Matters.</p>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">Present</div>
              <h3>Growing Team</h3>
              <p>The firm continues to expand its team and practice areas, maintaining the same dedication to personalized, results-oriented legal counsel.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Community */}
      <section className="about-community">
        <div
          ref={communitySection.ref}
          className={`about-community-inner ${communitySection.isVisible ? 'visible' : ''}`}
        >
          <h2>Community Involvement</h2>
          <p className="community-intro">
            Beyond the practice of law, Charlotte Janssen and the firm are actively involved
            in the Toronto community through leadership roles in business and civic organizations.
          </p>
          <div className="community-list">
            <div className="community-item">Yonge Bloor Bay Business Association (President)</div>
            <div className="community-item">Bloor Yorkville Business Improvement Area</div>
            <div className="community-item">Foundation for the Abilities Centre</div>
            <div className="community-item">Director of Private Corporations</div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="about-cta-inner">
          <h2>Ready to Work With Us?</h2>
          <p>Contact our team or learn more about the people behind Janssen Law.</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">
              Contact Us <FaArrowRight />
            </Link>
            <Link to="/team" className="btn-secondary">
              Meet Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
